import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

export interface OpenSlot {
  start_time: string;
  end_time: string;
  is_multi_day?: boolean;
  segments?: Array<{ date: string; start: string; end: string; minutes: number }>;
}
export interface AvailabilityResponse {
  duration_minutes: number;
  parts_eta_date: string | null;
  slots: OpenSlot[];
}

/** A same-day slot and a rollover (multi-day) slot can legitimately share the
 *  same `start_time` — they are distinct bookable options. This key
 *  disambiguates them for React list keys and for tracking selection, since
 *  `start_time` alone is not guaranteed unique within `slots`. */
export function slotKey(s: OpenSlot): string {
  return `${s.start_time}::${s.is_multi_day ? 'multi' : 'same'}`;
}

/** fetchAvailability + submit are injected so the same picker serves staff
 *  (apiRoot) and customer (customerPortalApi) callers. */
export function useSlotPicker(opts: {
  fetchAvailability: (startDate: string, endDate: string) => Promise<AvailabilityResponse>;
  submit: (startTime: string, segments?: OpenSlot['segments']) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<AvailabilityResponse | null>(null);
  const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);
  // Stores the composite slotKey() of the chosen option, not the raw
  // start_time — a same-day and a rollover slot can share a start_time.
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = dayjs().format('YYYY-MM-DD');
      const end = dayjs().add(30, 'day').format('YYYY-MM-DD');
      const res = await opts.fetchAvailability(start, end);
      setData(res);
      const earliest = res.slots[0] ? dayjs(res.slots[0].start_time) : null;
      setSelectedDay(earliest);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.fetchAvailability]);

  useEffect(() => { load(); }, [load]);

  const slotsForDay = (data?.slots || []).filter(
    (s) => selectedDay && dayjs(s.start_time).isSame(selectedDay, 'day')
  );
  const openDays = new Set((data?.slots || []).map((s) => dayjs(s.start_time).format('YYYY-MM-DD')));
  const partsEta = data?.parts_eta_date ? dayjs(data.parts_eta_date) : null;

  const selectedSlotObj = (data?.slots || []).find((s) => slotKey(s) === selectedSlot) || null;

  const confirm = async () => {
    if (!selectedSlotObj) return;
    setSubmitting(true);
    try {
      // Thread the selected slot's server-derived segment plan (when present,
      // e.g. a rollover slot) through to submit — otherwise a staff booking of
      // a coarse rollover quick-slot would only send start_time and the
      // backend would book one contiguous block through closed hours instead
      // of the multi-day segmented plan.
      await opts.submit(selectedSlotObj.start_time, selectedSlotObj.segments);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    loading, submitting, data, selectedDay, setSelectedDay, selectedSlot,
    setSelectedSlot, selectedSlotObj, slotsForDay, openDays, partsEta, confirm, reload: load,
  };
}
